/**
 * Eksport przekroju geologicznego do DXF - WERSJA 2 (EKSPERYMENTALNE)
 * Używa biblioteki @tarikjabiri/dxf
 *
 * Różnice vs v1:
 * - Warstwy gruntu (połączenia/interpolacje) rysowane jako PIERWSZE (na dnie w z-order)
 * - Pozostałe elementy nad nimi: soczewki, topografia, otwory, rysunki, siatka, skale
 *
 * Flaga DXF_EXPORT_V2_ENABLED pozwala wyłączyć funkcjonalność.
 */

import {
  DxfWriter,
  point2d,
  point3d,
  Colors,
  Units,
  LWPolylineFlags,
  HatchBoundaryPaths,
  HatchPolylineBoundary,
  HatchPredefinedPatterns,
  vertex,
  pattern,
  aciHex,
} from '@tarikjabiri/dxf';

import {
  SVG_TOP_MARGIN,
  SVG_LEFT_MARGIN,
  BOREHOLE_HALF_WIDTH,
} from './constants';

import {
  getConnectionKey as getConnectionKeyUtil,
  getConnectionPointKey as getConnectionPointKeyUtil,
  getTotalThickness,
} from './svgHelpers';

import { getMaxDepth as getMaxDepthUtil } from './svgDimensions';

// ============================================================
// FLAGA ON/OFF — ustaw false żeby wyłączyć eksport DXF v2
// ============================================================
export const DXF_EXPORT_V2_ENABLED = true;

// ============================================================
// ACI color table — mapowanie index → [R, G, B]
// Budujemy z funkcji aciHex() z biblioteki
// ============================================================
const ACI_RGB = [];
for (let i = 0; i <= 255; i++) {
  try {
    const hex = aciHex(i);
    if (hex && hex.length === 6) {
      ACI_RGB[i] = [
        parseInt(hex.substring(0, 2), 16),
        parseInt(hex.substring(2, 4), 16),
        parseInt(hex.substring(4, 6), 16),
      ];
    } else {
      ACI_RGB[i] = [255, 255, 255];
    }
  } catch {
    ACI_RGB[i] = [255, 255, 255];
  }
}

/** HSL (0..360, 0..100%, 0..100%) → RGB (0..255). Standardowa formula. */
function hslToRgb(h, s, l) {
  s /= 100; l /= 100;
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs((h / 60) % 2 - 1));
  const m = l - c / 2;
  let r, g, b;
  if (h < 60)       [r, g, b] = [c, x, 0];
  else if (h < 120) [r, g, b] = [x, c, 0];
  else if (h < 180) [r, g, b] = [0, c, x];
  else if (h < 240) [r, g, b] = [0, x, c];
  else if (h < 300) [r, g, b] = [x, 0, c];
  else              [r, g, b] = [c, 0, x];
  return [
    Math.round((r + m) * 255),
    Math.round((g + m) * 255),
    Math.round((b + m) * 255),
  ];
}

/**
 * Konwertuje kolor (hex #RRGGBB lub `hsl(h, s%, l%)`) na najbliższy ACI index (1-255).
 * Wspiera HSL bo freeDraw polygony używają tego formatu.
 */
function hexToAci(hex) {
  if (!hex || typeof hex !== 'string') return 7; // white

  let r, g, b;
  const hslMatch = hex.match(/^hsl\(\s*(\d+(?:\.\d+)?)\s*,\s*(\d+(?:\.\d+)?)%\s*,\s*(\d+(?:\.\d+)?)%\s*\)$/i);
  if (hslMatch) {
    [r, g, b] = hslToRgb(parseFloat(hslMatch[1]), parseFloat(hslMatch[2]), parseFloat(hslMatch[3]));
  } else {
    const h = hex.replace('#', '');
    if (h.length < 6) return 7;
    r = parseInt(h.substring(0, 2), 16);
    g = parseInt(h.substring(2, 4), 16);
    b = parseInt(h.substring(4, 6), 16);
    if (Number.isNaN(r) || Number.isNaN(g) || Number.isNaN(b)) return 7;
  }

  let bestIndex = 7;
  let bestDist = Infinity;

  for (let i = 1; i <= 255; i++) {
    const [ar, ag, ab] = ACI_RGB[i];
    const dist = (r - ar) ** 2 + (g - ag) ** 2 + (b - ab) ** 2;
    if (dist < bestDist) {
      bestDist = dist;
      bestIndex = i;
    }
  }
  return bestIndex;
}

// ============================================================
// Mapowanie wzorów geologicznych → HatchPredefinedPatterns
// ============================================================
const PATTERN_MAP = {
  'sand': HatchPredefinedPatterns.DOTS,
  'silty-sand': HatchPredefinedPatterns.DOTS,
  'clayey-sand': HatchPredefinedPatterns.DOTS,
  'gravel': HatchPredefinedPatterns.GRAVEL,
  'sandy-gravel': HatchPredefinedPatterns.GRAVEL,
  'clay': HatchPredefinedPatterns.LINE,
  'silt': HatchPredefinedPatterns.LINE,
  'sandy-clay': HatchPredefinedPatterns.CLAY,
  'rock': HatchPredefinedPatterns.CROSS,
  'peat': HatchPredefinedPatterns.ANSI31,
  'alluvium': HatchPredefinedPatterns.MUDST,
  'debris': HatchPredefinedPatterns.AR_SAND,
  // Legacy lens patterns
  'dots': HatchPredefinedPatterns.DOTS,
  'horizontal': HatchPredefinedPatterns.LINE,
  'diagonal': HatchPredefinedPatterns.ANSI31,
  'cross': HatchPredefinedPatterns.CROSS,
  'none': HatchPredefinedPatterns.SOLID,
};

function getHatchPattern(appPattern) {
  return PATTERN_MAP[appPattern] || HatchPredefinedPatterns.SOLID;
}

// ============================================================
// Konwersja współrzędnych SVG → metry DXF
// ============================================================

/** SVG pixel X → odległość w metrach */
function svgXToM(svgX, horizontalScale) {
  return (svgX - SVG_LEFT_MARGIN) / horizontalScale;
}

/**
 * Mnożnik skali pionowej w DXF v2.
 * 1 = brak przewyższenia (1 metr = 1 jednostka DXF, tak jak w v1).
 */
const VERTICAL_SCALE_FACTOR = 1;

/** SVG pixel Y → rzędna (elevation) w metrach. Y_dxf rośnie w górę. */
function svgYToElev(svgY, maxDepth, pixelsPerMeter) {
  return (maxDepth - (svgY - SVG_TOP_MARGIN) / pixelsPerMeter) * VERTICAL_SCALE_FACTOR;
}

// ============================================================
// Aproksymacja krzywej Béziera kubicznej (C) na N odcinków
// ============================================================

/**
 * Bézier kubiczny z jednym "control point" (tak jak w createBezierPath):
 * cp1 = start + (control - start) * 0.5
 * cp2 = end + (control - end) * 0.5
 */
function sampleCubicBezier(x1, y1, x2, y2, controlPoint, numSegments = 20) {
  if (!controlPoint) {
    return [{ x: x1, y: y1 }, { x: x2, y: y2 }];
  }

  const cp1x = x1 + (controlPoint.x - x1) * 0.5;
  const cp1y = y1 + (controlPoint.y - y1) * 0.5;
  const cp2x = x2 + (controlPoint.x - x2) * 0.5;
  const cp2y = y2 + (controlPoint.y - y2) * 0.5;

  const points = [];
  for (let i = 0; i <= numSegments; i++) {
    const t = i / numSegments;
    const mt = 1 - t;
    const x = mt * mt * mt * x1 + 3 * mt * mt * t * cp1x + 3 * mt * t * t * cp2x + t * t * t * x2;
    const y = mt * mt * mt * y1 + 3 * mt * mt * t * cp1y + 3 * mt * t * t * cp2y + t * t * t * y2;
    points.push({ x, y });
  }
  return points;
}

// ============================================================
// Pomocnik: dodaj zamknięty LWPOLYLINE z hatcha
// ============================================================
function addClosedPolyWithHatch(dxf, meterPoints, layerName, colorNum, appPattern, hatchScale, useHatchPattern = true) {
  if (meterPoints.length < 3) return;

  // Outline
  dxf.addLWPolyline(
    meterPoints.map(p => ({ point: point2d(p.x, p.y) })),
    { flags: LWPolylineFlags.Closed, layerName, colorNumber: colorNum }
  );

  // Hatch — jesli useHatchPattern === false, zamiast wzoru daj SOLID (samo kolorowe wypelnienie).
  try {
    const polyBoundary = new HatchPolylineBoundary();
    meterPoints.forEach(p => polyBoundary.add(vertex(p.x, p.y)));
    const boundary = new HatchBoundaryPaths();
    boundary.addPolylineBoundary(polyBoundary);

    const patternName = useHatchPattern ? getHatchPattern(appPattern) : HatchPredefinedPatterns.SOLID;
    const hatch = dxf.addHatch(
      boundary,
      pattern({ name: patternName, scale: hatchScale || 1 }),
      { layerName }
    );
    hatch.colorNumber = colorNum;
  } catch (e) {
    // Hatch może nie zadziałać dla niektórych kształtów — ignorujemy
    console.warn('DXF hatch error:', e.message);
  }
}

// ============================================================
// EKSPORT OTWORÓW
// ============================================================
function exportBoreholes(dxf, params) {
  const { boreholes, maxDepth, horizontalScale } = params;
  const halfWidthM = BOREHOLE_HALF_WIDTH / horizontalScale;

  boreholes.forEach(borehole => {
    const bx = borehole.distance;
    // Rzędna terenu otworu × VERTICAL_SCALE_FACTOR (skala pionowa DXF)
    const boreholeTopElev = borehole.depth * VERTICAL_SCALE_FACTOR;

    // Nazwa otworu
    dxf.addText(point3d(bx, boreholeTopElev + 1.5, 0), 0.6, borehole.name || 'Otwór', {
      layerName: 'WARSTWY_OPIS',
    });

    // Rzędna pod nazwą
    dxf.addText(point3d(bx, boreholeTopElev + 0.5, 0), 0.35, borehole.depth.toFixed(2), {
      layerName: 'WARSTWY_OPIS',
      colorNumber: 8, // gray
    });

    let cumulativeDepth = 0;

    borehole.layers.forEach(layer => {
      const layerThicknessScaled = layer.thickness * VERTICAL_SCALE_FACTOR;
      const layerTopElev = boreholeTopElev - cumulativeDepth * VERTICAL_SCALE_FACTOR;
      const layerBottomElev = layerTopElev - layerThicknessScaled;
      cumulativeDepth += layer.thickness;

      const colorNum = hexToAci(layer.color);

      if (params.showBoreholeRectangles) {
        // Prostokąt warstwy
        const rectPts = [
          { x: bx - halfWidthM, y: layerTopElev },
          { x: bx + halfWidthM, y: layerTopElev },
          { x: bx + halfWidthM, y: layerBottomElev },
          { x: bx - halfWidthM, y: layerBottomElev },
        ];
        addClosedPolyWithHatch(dxf, rectPts, 'WARSTWY_WYPELNIENIE', colorNum, layer.pattern || 'none', halfWidthM * 0.3, params.useHatchPatterns);

        // Kontur otworu
        dxf.addLWPolyline(
          rectPts.map(p => ({ point: point2d(p.x, p.y) })),
          { flags: LWPolylineFlags.Closed, layerName: 'OTWORY', colorNumber: 7 }
        );
      }

      // Etykiety
      const centerElev = (layerTopElev + layerBottomElev) / 2;
      // Wysokość tekstu skalowana razem z osią Y (thickness × skala × 0.25)
      const textH = Math.max(0.15 * VERTICAL_SCALE_FACTOR, Math.min(0.4 * VERTICAL_SCALE_FACTOR, layerThicknessScaled * 0.25));

      if (params.showSoilName && layer.name) {
        dxf.addText(point3d(bx, centerElev, 0), textH, layer.name, {
          layerName: 'WARSTWY_OPIS',
          colorNumber: colorNum,
        });
      }

      if (params.showThickness) {
        dxf.addText(point3d(bx, centerElev - textH * 1.5, 0), textH * 0.7, `${layer.thickness}m`, {
          layerName: 'WARSTWY_OPIS',
          colorNumber: 8,
        });
      }

      if (params.showDepths) {
        dxf.addText(
          point3d(bx + halfWidthM + 0.3, layerBottomElev, 0),
          textH * 0.7,
          cumulativeDepth.toFixed(1),
          { layerName: 'GLEBOKOSCI', colorNumber: 8 }
        );
      }

      if (params.showSoilState && layer.soilState) {
        dxf.addText(
          point3d(bx - halfWidthM - 0.3, centerElev, 0),
          textH * 0.6,
          layer.soilState,
          { layerName: 'WARSTWY_OPIS', colorNumber: 8 }
        );
      }

      if (params.showGeotechnicalLayer && layer.geotechnicalLayer) {
        dxf.addText(
          point3d(bx - halfWidthM - 0.3, centerElev - textH, 0),
          textH * 0.6,
          layer.geotechnicalLayer,
          { layerName: 'WARSTWY_OPIS', colorNumber: 8 }
        );
      }
    });
  });
}

// ============================================================
// EKSPORT POŁĄCZEŃ (interpolacje Bézier)
// ============================================================
function exportConnections(dxf, params) {
  console.log('[DXF v2] exportConnections start, connectLayers=', params.connectLayers);
  if (!params.connectLayers) return;

  const {
    maxDepth, pixelsPerMeter, horizontalScale,
    interpolationPoints, connectionPointAdjustments,
  } = params;

  const connections = params.findLayerConnections();
  console.log('[DXF v2] findLayerConnections() →', connections.length, 'połączeń');

  connections.forEach(conn => {
    const x1svg = SVG_LEFT_MARGIN + conn.fromBorehole.distance * horizontalScale;
    const x2svg = SVG_LEFT_MARGIN + conn.toBorehole.distance * horizontalScale;

    // Connection point keys
    const pkFT = getConnectionPointKeyUtil(conn.fromBorehole.id, conn.toBorehole.id, conn.name, true, true, conn.connectionId);
    const pkFB = getConnectionPointKeyUtil(conn.fromBorehole.id, conn.toBorehole.id, conn.name, false, true, conn.connectionId);
    const pkTT = getConnectionPointKeyUtil(conn.fromBorehole.id, conn.toBorehole.id, conn.name, true, false, conn.connectionId);
    const pkTB = getConnectionPointKeyUtil(conn.fromBorehole.id, conn.toBorehole.id, conn.name, false, false, conn.connectionId);

    const getAdj = (key, axis) => {
      const adj = connectionPointAdjustments[key];
      if (!adj) return 0;
      return typeof adj === 'object' ? (adj[axis] || 0) : (axis === 'y' ? adj : 0);
    };

    // SVG coordinates (pixels) — same as in GeologicalProfile.jsx rendering
    const svgX1Top = x1svg + getAdj(pkFT, 'x');
    const svgX1Bottom = x1svg + getAdj(pkFB, 'x');
    const svgX2Top = x2svg + getAdj(pkTT, 'x');
    const svgX2Bottom = x2svg + getAdj(pkTB, 'x');

    const svgY1Top = SVG_TOP_MARGIN + conn.fromTop * pixelsPerMeter + getAdj(pkFT, 'y');
    const svgY1Bottom = SVG_TOP_MARGIN + conn.fromBottom * pixelsPerMeter + getAdj(pkFB, 'y');
    const svgY2Top = SVG_TOP_MARGIN + conn.toTop * pixelsPerMeter + getAdj(pkTT, 'y');
    const svgY2Bottom = SVG_TOP_MARGIN + conn.toBottom * pixelsPerMeter + getAdj(pkTB, 'y');

    // Interpolation control points (in SVG pixels)
    const ckTop = getConnectionKeyUtil(conn.fromBorehole.id, conn.toBorehole.id, conn.name, 'top', conn.connectionId);
    const ckBottom = getConnectionKeyUtil(conn.fromBorehole.id, conn.toBorehole.id, conn.name, 'bottom', conn.connectionId);
    const ckLeft = getConnectionKeyUtil(conn.fromBorehole.id, conn.toBorehole.id, conn.name, 'left', conn.connectionId);
    const ckRight = getConnectionKeyUtil(conn.fromBorehole.id, conn.toBorehole.id, conn.name, 'right', conn.connectionId);

    const cpTop = interpolationPoints[ckTop] || null;
    const cpBottom = interpolationPoints[ckBottom] || null;
    const cpLeft = interpolationPoints[ckLeft] || null;
    const cpRight = interpolationPoints[ckRight] || null;

    // Sample 4 Bézier edges IN SVG PIXELS, then convert to meters
    const topPtsSvg = sampleCubicBezier(svgX1Top, svgY1Top, svgX2Top, svgY2Top, cpTop, 20);
    const rightPtsSvg = sampleCubicBezier(svgX2Top, svgY2Top, svgX2Bottom, svgY2Bottom, cpRight, 10);
    const bottomPtsSvg = sampleCubicBezier(svgX2Bottom, svgY2Bottom, svgX1Bottom, svgY1Bottom, cpBottom, 20);
    const leftPtsSvg = sampleCubicBezier(svgX1Bottom, svgY1Bottom, svgX1Top, svgY1Top, cpLeft, 10);

    // Combine into closed polygon, remove duplicate endpoints
    const allSvg = [
      ...topPtsSvg,
      ...rightPtsSvg.slice(1),
      ...bottomPtsSvg.slice(1),
      ...leftPtsSvg.slice(1),
    ];

    // Convert to meters
    const meterPts = allSvg.map(p => ({
      x: svgXToM(p.x, horizontalScale),
      y: svgYToElev(p.y, maxDepth, pixelsPerMeter),
    }));

    // Fallback: ACI 7 (white) jest niewidoczny na ciemnym tle viewerów CAD,
    // wiec puste/biale conn.color zamieniamy na jasny szary.
    const safeColor = (!conn.color || /^#?f{6}$/i.test(conn.color.replace('#', '')))
      ? '#CCCCCC'
      : conn.color;
    const colorNum = hexToAci(safeColor);
    console.log('[DXF v2] conn:', conn.name, 'color:', conn.color, '→ safe:', safeColor, 'ACI:', colorNum);
    addClosedPolyWithHatch(dxf, meterPts, 'POLACZENIA', colorNum, conn.pattern || 'none', 1, params.useHatchPatterns);
  });
}

// ============================================================
// EKSPORT SOCZEWEK
// ============================================================
function exportLenses(dxf, params) {
  if (!params.showLenses || !params.lenses) return;

  const { maxDepth, pixelsPerMeter, horizontalScale, lenses } = params;

  lenses.forEach(lens => {
    const colorNum = hexToAci(lens.color);

    if (lens.shape === 'circle') {
      const cx = svgXToM(lens.centerX, horizontalScale);
      const cy = svgYToElev(lens.centerY, maxDepth, pixelsPerMeter);
      const rx = lens.radius / horizontalScale;
      // Promień pionowy × VERTICAL_SCALE_FACTOR (skala pionowa DXF)
      const ry = (lens.radius / pixelsPerMeter) * VERTICAL_SCALE_FACTOR;

      // Aproksymacja elipsy 36-punktowym polyline
      const pts = [];
      const n = 36;
      for (let i = 0; i < n; i++) {
        const angle = (2 * Math.PI * i) / n;
        pts.push({ x: cx + rx * Math.cos(angle), y: cy + ry * Math.sin(angle) });
      }
      addClosedPolyWithHatch(dxf, pts, 'SOCZEWKI', colorNum, lens.pattern || 'none', 0.5, params.useHatchPatterns);

      // Nazwa
      if (lens.name) {
        dxf.addText(point3d(cx, cy, 0), 0.3 * VERTICAL_SCALE_FACTOR, lens.name, { layerName: 'SOCZEWKI' });
      }

    } else if (lens.shape === 'rectangle') {
      const rectWidth = Math.abs(lens.width);
      const rectHeight = Math.abs(lens.height);
      const rectX = lens.width < 0 ? lens.x + lens.width : lens.x;
      const rectY = lens.height < 0 ? lens.y + lens.height : lens.y;

      const tl = { x: svgXToM(rectX, horizontalScale), y: svgYToElev(rectY, maxDepth, pixelsPerMeter) };
      const tr = { x: svgXToM(rectX + rectWidth, horizontalScale), y: svgYToElev(rectY, maxDepth, pixelsPerMeter) };
      const br = { x: svgXToM(rectX + rectWidth, horizontalScale), y: svgYToElev(rectY + rectHeight, maxDepth, pixelsPerMeter) };
      const bl = { x: svgXToM(rectX, horizontalScale), y: svgYToElev(rectY + rectHeight, maxDepth, pixelsPerMeter) };

      addClosedPolyWithHatch(dxf, [tl, tr, br, bl], 'SOCZEWKI', colorNum, lens.pattern || 'none', 0.5, params.useHatchPatterns);

      // Nazwa
      if (lens.name) {
        const cx = (tl.x + br.x) / 2;
        const cy = (tl.y + br.y) / 2;
        dxf.addText(point3d(cx, cy, 0), 0.3 * VERTICAL_SCALE_FACTOR, lens.name, { layerName: 'SOCZEWKI' });
      }

    } else if (lens.shape === 'polygon' && lens.points && lens.points.length >= 3) {
      const mPts = lens.points.map(p => ({
        x: svgXToM(p.x, horizontalScale),
        y: svgYToElev(p.y, maxDepth, pixelsPerMeter),
      }));
      addClosedPolyWithHatch(dxf, mPts, 'SOCZEWKI', colorNum, lens.pattern || 'none', 0.5, params.useHatchPatterns);

      // Nazwa (centroid)
      if (lens.name) {
        const cx = mPts.reduce((s, p) => s + p.x, 0) / mPts.length;
        const cy = mPts.reduce((s, p) => s + p.y, 0) / mPts.length;
        dxf.addText(point3d(cx, cy, 0), 0.3 * VERTICAL_SCALE_FACTOR, lens.name, { layerName: 'SOCZEWKI' });
      }
    }
  });
}

// ============================================================
// EKSPORT TOPOGRAFII NMT
// ============================================================
function exportTerrain(dxf, params) {
  if (!params.showTerrain || !params.terrainProfile || params.terrainProfile.length < 2) return;

  // terrainProfile ma odległość i rzędną w metrach — Y × VERTICAL_SCALE_FACTOR
  const pts = params.terrainProfile;
  const sf = VERTICAL_SCALE_FACTOR;

  dxf.addLWPolyline(
    pts.map(p => ({ point: point2d(p.distance, p.elevation * sf) })),
    { layerName: 'TOPOGRAFIA', colorNumber: Colors.Red }
  );

  // Etykiety rzędnych przy otworach (tekst pokazuje rzeczywiste metry)
  params.boreholes.forEach(borehole => {
    const terrainPt = pts.find(p => Math.abs(p.distance - borehole.distance) < 0.5);
    if (terrainPt) {
      dxf.addText(
        point3d(terrainPt.distance, terrainPt.elevation * sf + 0.5 * sf, 0),
        0.25 * sf,
        `${terrainPt.elevation.toFixed(1)} m n.p.m.`,
        { layerName: 'TOPOGRAFIA', colorNumber: Colors.Red }
      );
    }
  });
}

// ============================================================
// EKSPORT RYSUNKÓW
// ============================================================
function exportDrawings(dxf, params) {
  if (!params.drawings || params.drawings.length === 0) return;

  const { maxDepth, pixelsPerMeter, horizontalScale } = params;

  params.drawings.forEach(d => {
    const x1 = svgXToM(d.startX, horizontalScale);
    const y1 = svgYToElev(d.startY, maxDepth, pixelsPerMeter);
    const x2 = svgXToM(d.endX, horizontalScale);
    const y2 = svgYToElev(d.endY, maxDepth, pixelsPerMeter);
    const colorNum = hexToAci(d.color || '#FF0000');

    switch (d.type) {
      case 'line':
        dxf.addLine(point3d(x1, y1, 0), point3d(x2, y2, 0), { layerName: 'RYSUNKI', colorNumber: colorNum });
        break;

      case 'arrow': {
        dxf.addLine(point3d(x1, y1, 0), point3d(x2, y2, 0), { layerName: 'RYSUNKI', colorNumber: colorNum });
        // Grot strzałki
        const angle = Math.atan2(y2 - y1, x2 - x1);
        const arrowLen = 0.3;
        dxf.addLWPolyline([
          { point: point2d(x2, y2) },
          { point: point2d(x2 - arrowLen * Math.cos(angle - Math.PI / 6), y2 - arrowLen * Math.sin(angle - Math.PI / 6)) },
          { point: point2d(x2 - arrowLen * Math.cos(angle + Math.PI / 6), y2 - arrowLen * Math.sin(angle + Math.PI / 6)) },
        ], { flags: LWPolylineFlags.Closed, layerName: 'RYSUNKI', colorNumber: colorNum });
        break;
      }

      case 'rect': {
        const minX = Math.min(x1, x2), minY = Math.min(y1, y2);
        const maxX = Math.max(x1, x2), maxY = Math.max(y1, y2);
        dxf.addLWPolyline([
          { point: point2d(minX, minY) },
          { point: point2d(maxX, minY) },
          { point: point2d(maxX, maxY) },
          { point: point2d(minX, maxY) },
        ], { flags: LWPolylineFlags.Closed, layerName: 'RYSUNKI', colorNumber: colorNum });
        break;
      }

      case 'circle': {
        const radiusPx = Math.sqrt((d.endX - d.startX) ** 2 + (d.endY - d.startY) ** 2);
        const radiusM = radiusPx / horizontalScale;
        dxf.addCircle(point3d(x1, y1, 0), radiusM, { layerName: 'RYSUNKI', colorNumber: colorNum });
        break;
      }

      case 'text': {
        // Wysokość tekstu skalowana razem z osią Y (18 px / pixelsPerMeter × skala)
        const textH = (18 / pixelsPerMeter) * VERTICAL_SCALE_FACTOR;
        dxf.addText(point3d(x1, y1, 0), textH, d.text || 'Tekst', { layerName: 'RYSUNKI', colorNumber: colorNum });
        break;
      }

      case 'measure': {
        dxf.addLine(point3d(x1, y1, 0), point3d(x2, y2, 0), { layerName: 'RYSUNKI', colorNumber: colorNum });
        const midX = (x1 + x2) / 2;
        const midY = (y1 + y2) / 2;
        // distM w metrach rzeczywistych — trzeba odwrócić skalę pionową
        const dyReal = (y2 - y1) / VERTICAL_SCALE_FACTOR;
        const distM = Math.sqrt((x2 - x1) ** 2 + dyReal ** 2);
        dxf.addText(point3d(midX, midY + 0.2 * VERTICAL_SCALE_FACTOR, 0), 0.25 * VERTICAL_SCALE_FACTOR, `${distM.toFixed(2)}m`, { layerName: 'RYSUNKI', colorNumber: colorNum });
        break;
      }

      case 'pencil': {
        if (d.points && d.points.length >= 2) {
          const pts = d.points.map(p => ({
            point: point2d(svgXToM(p.x, horizontalScale), svgYToElev(p.y, maxDepth, pixelsPerMeter)),
          }));
          dxf.addLWPolyline(pts, { layerName: 'RYSUNKI', colorNumber: colorNum });
        }
        break;
      }

      default:
        break;
    }
  });
}

// ============================================================
// EKSPORT FREEDRAW POLYGONÓW
// ============================================================
function exportFreeDrawPolygons(dxf, params) {
  if (!params.connectLayers || !params.manualConnections) return;

  const { maxDepth, pixelsPerMeter, horizontalScale, manualConnections, resolvePointSvg } = params;

  const freeDrawConns = manualConnections.filter(mc => mc.fromLayerId === 'freeDraw' && mc.points);
  console.log('[DXF v2] freeDraw polygons →', freeDrawConns.length, 'sztuk');

  freeDrawConns.forEach(mc => {
    console.log('[DXF v2] freeDraw:', mc.name, 'color:', mc.color, 'pattern:', mc.pattern);
    const svgPoints = mc.points.map(p => resolvePointSvg(p)).filter(Boolean);
    if (svgPoints.length < 3) return;

    const mPts = svgPoints.map(p => ({
      x: svgXToM(p.x, horizontalScale),
      y: svgYToElev(p.y, maxDepth, pixelsPerMeter),
    }));

    const colorNum = hexToAci(mc.color || '#CCCCCC');
    addClosedPolyWithHatch(dxf, mPts, 'FREEDRAW', colorNum, mc.pattern || 'none', 1, params.useHatchPatterns);
  });
}

// ============================================================
// EKSPORT SIATKI
// ============================================================
function exportGrid(dxf, params) {
  if (!params.showGrid) return;

  const { boreholes, maxDepth } = params;
  if (!boreholes || boreholes.length === 0) return;

  const maxDist = Math.max(...boreholes.map(b => b.distance || 0));
  const maxThickness = Math.max(...boreholes.map(b => getTotalThickness(b.layers)));
  // Skala pionowa DXF v2 — Y × VERTICAL_SCALE_FACTOR
  const maxDepthY = maxDepth * VERTICAL_SCALE_FACTOR;
  const minElevY = (maxDepth - maxThickness) * VERTICAL_SCALE_FACTOR;
  const marginY = 3 * VERTICAL_SCALE_FACTOR;

  // Grid — X co 5m (metry), Y co 5m rzeczywiste = 50 jednostek DXF
  const gridStepX = 5;
  const gridStepY = 5 * VERTICAL_SCALE_FACTOR;

  // Vertical lines
  for (let x = 0; x <= maxDist + gridStepX; x += gridStepX) {
    dxf.addLine(
      point3d(x, maxDepthY + marginY, 0),
      point3d(x, minElevY - marginY, 0),
      { layerName: 'SIATKA', colorNumber: 8 }
    );
  }

  // Horizontal lines
  for (let y = Math.floor(minElevY) - marginY; y <= maxDepthY + marginY; y += gridStepY) {
    dxf.addLine(
      point3d(-2, y, 0),
      point3d(maxDist + gridStepX, y, 0),
      { layerName: 'SIATKA', colorNumber: 8 }
    );
  }
}

// ============================================================
// EKSPORT OSI X i Y (skale pionowa i pozioma)
// ============================================================
function exportScales(dxf, params) {
  const { boreholes, maxDepth } = params;
  if (!boreholes || boreholes.length === 0) return;

  const minElev = Math.min(...boreholes.map(b => b.depth - getTotalThickness(b.layers)));
  const sortedBoreholes = [...boreholes].sort((a, b) => a.distance - b.distance);
  const maxDist = Math.max(...boreholes.map(b => b.distance || 0));

  // --- OŚ Y (pionowa) — rzędne, po prawej stronie przekroju ---
  const scaleX = maxDist + 3; // 3m na prawo od ostatniego otworu
  const elevTop = Math.ceil(maxDepth);
  const elevBottom = Math.floor(minElev);
  const range = elevTop - elevBottom;
  const labelInterval = range > 50 ? 10 : 5;
  const sf = VERTICAL_SCALE_FACTOR;
  const textScale = sf; // wysokość tekstu skalowana z osią Y

  // Linia główna osi Y (Y × sf)
  dxf.addLine(
    point3d(scaleX, elevTop * sf, 0),
    point3d(scaleX, elevBottom * sf, 0),
    { layerName: 'SKALA', colorNumber: 7 }
  );

  // Kreski i etykiety (Y × sf, etykiety tekstowe pokazują rzeczywiste metry)
  for (let elev = elevBottom; elev <= elevTop; elev += 1) {
    const isLabel = elev % labelInterval === 0;
    const tickLen = isLabel ? 0.5 : 0.2;

    dxf.addLine(
      point3d(scaleX - tickLen, elev * sf, 0),
      point3d(scaleX + tickLen, elev * sf, 0),
      { layerName: 'SKALA', colorNumber: 7 }
    );

    if (isLabel) {
      dxf.addText(
        point3d(scaleX + 0.8, elev * sf, 0),
        0.4 * textScale,
        `${elev}`,
        { layerName: 'SKALA', colorNumber: 7 }
      );
    }
  }

  // Etykieta osi Y
  dxf.addText(
    point3d(scaleX + 0.8, (elevTop + 1) * sf, 0),
    0.35 * textScale,
    'm n.p.m.',
    { layerName: 'SKALA', colorNumber: 7 }
  );

  // --- OŚ X (pozioma) — odległości, pod przekrojem ---
  const scaleY = (elevBottom - 2) * sf; // 2m poniżej najniższego punktu (× sf)

  if (sortedBoreholes.length > 0) {
    const firstDist = sortedBoreholes[0].distance;
    const lastDist = sortedBoreholes[sortedBoreholes.length - 1].distance;

    // Linia główna osi X
    dxf.addLine(
      point3d(firstDist, scaleY, 0),
      point3d(lastDist, scaleY, 0),
      { layerName: 'SKALA', colorNumber: 7 }
    );

    // Kreski przy otworach + etykiety odległości między nimi
    sortedBoreholes.forEach((borehole, idx) => {
      const x = borehole.distance;

      // Kreska (pół-metra w górę/dół × sf)
      dxf.addLine(
        point3d(x, scaleY - 0.5 * sf, 0),
        point3d(x, scaleY + 0.5 * sf, 0),
        { layerName: 'SKALA', colorNumber: 7 }
      );

      // Nazwa otworu pod osią
      dxf.addText(
        point3d(x, scaleY - 1.2 * sf, 0),
        0.3 * textScale,
        borehole.name || '',
        { layerName: 'SKALA', colorNumber: 7 }
      );

      // Odległość między otworami
      if (idx < sortedBoreholes.length - 1) {
        const nextBorehole = sortedBoreholes[idx + 1];
        const dist = nextBorehole.distance - borehole.distance;
        const midX = (x + nextBorehole.distance) / 2;

        dxf.addText(
          point3d(midX, scaleY + 1 * sf, 0),
          0.35 * textScale,
          `${dist.toFixed(1)}m`,
          { layerName: 'SKALA', colorNumber: 7 }
        );
      }
    });
  }

  // Etykieta osi X
  dxf.addText(
    point3d(maxDist + 1, scaleY - 1.2 * sf, 0),
    0.35 * textScale,
    'odl. [m]',
    { layerName: 'SKALA', colorNumber: 7 }
  );
}

// ============================================================
// EKSPORT WODY — symbole przy otworach (trójkąty + sączenie)
// ============================================================
function exportWaterLevels(dxf, params) {
  if (!params.showWater) return;
  const { boreholes, horizontalScale } = params;
  if (!boreholes) return;

  // Symbol ma być po lewej stronie słupka. Offset w metrach rzeczywistych
  // (BOREHOLE_HALF_WIDTH to piksele → dzielimy przez horizontalScale na metry).
  const halfWidthM = BOREHOLE_HALF_WIDTH / horizontalScale;
  const symbolOffsetM = halfWidthM + 0.3; // 0.3 m na lewo od słupka
  const symbolSizeM = 0.6;                // rozmiar trójkąta / fali
  const textH = 0.3;
  const BLUE = 5; // ACI index 5 = blue

  boreholes.forEach(borehole => {
    if (!borehole.waterLevels || borehole.waterLevels.length === 0) return;

    const topElev = borehole.depth;
    const cx = borehole.distance - symbolOffsetM;

    borehole.waterLevels.forEach(wl => {
      if (wl.type === 'stabilized-drilled') {
        // depthFrom = zwierciadło ustabilizowane (trójkąt wypełniony)
        // depthTo   = zwierciadło nawiercone   (trójkąt pusty)
        const yStab = topElev - wl.depthFrom;
        const yDril = topElev - wl.depthTo;

        // Łamana "zygzak" łącząca oba trójkąty:
        // lewy-góra (yStab) → środek-góra (yStab) → środek-dół (yDril) → prawy-dół (yDril)
        dxf.addLWPolyline(
          [
            { point: point2d(cx - symbolSizeM, yStab) },
            { point: point2d(cx, yStab) },
            { point: point2d(cx, yDril) },
            { point: point2d(cx + symbolSizeM, yDril) },
          ],
          { layerName: 'WODA', colorNumber: BLUE }
        );

        // Trójkąt ustabilizowany — wypełniony (SOLID)
        const triStab = [
          { x: cx,                y: yStab },                   // szczyt na linii
          { x: cx - symbolSizeM,  y: yStab + symbolSizeM },     // górny-lewy
          { x: cx + symbolSizeM,  y: yStab + symbolSizeM },     // górny-prawy
        ];
        // 'none' → SOLID hatch = wypełniony trójkąt (konwencja z tego pliku)
        addClosedPolyWithHatch(dxf, triStab, 'WODA', BLUE, 'none', 0.3);

        // Trójkąt nawiercony — pusty (tylko kontur)
        dxf.addLWPolyline(
          [
            { point: point2d(cx,                yDril) },
            { point: point2d(cx - symbolSizeM,  yDril + symbolSizeM) },
            { point: point2d(cx + symbolSizeM,  yDril + symbolSizeM) },
          ],
          { flags: LWPolylineFlags.Closed, layerName: 'WODA', colorNumber: BLUE }
        );

        // Etykiety głębokości
        dxf.addText(
          point3d(cx - symbolSizeM * 0.7, yStab - textH * 1.2, 0),
          textH,
          wl.depthFrom.toFixed(1),
          { layerName: 'WODA', colorNumber: BLUE }
        );
        dxf.addText(
          point3d(cx + symbolSizeM * 0.7, yDril - textH * 1.2, 0),
          textH,
          wl.depthTo.toFixed(1),
          { layerName: 'WODA', colorNumber: BLUE }
        );
      } else if (wl.type === 'seepage') {
        // Sączenie — falista linia o 4 "zębach"
        const yLine = topElev - wl.depth;
        const wavePts = buildWave(cx, yLine, symbolSizeM * 1.5, symbolSizeM * 0.25, 4);
        dxf.addLWPolyline(
          wavePts.map(p => ({ point: point2d(p.x, p.y) })),
          { layerName: 'WODA', colorNumber: BLUE }
        );

        dxf.addText(
          point3d(cx, yLine - textH * 1.5, 0),
          textH,
          wl.depth.toFixed(1),
          { layerName: 'WODA', colorNumber: BLUE }
        );
      }
    });
  });
}

/**
 * Buduje polilinię falistą (aproksymacja sinusa) wycentrowaną w (cx, cy).
 * halfWidth — połowa szerokości całej fali (m)
 * amplitude — amplituda pionowa fali (m)
 * waves     — liczba "zębów" (pełnych okresów)
 */
function buildWave(cx, cy, halfWidth, amplitude, waves) {
  const samplesPerWave = 8;
  const totalSamples = waves * samplesPerWave + 1;
  const pts = [];
  for (let i = 0; i < totalSamples; i++) {
    const t = i / (totalSamples - 1); // 0..1
    const x = cx - halfWidth + t * (2 * halfWidth);
    const y = cy + amplitude * Math.sin(t * waves * 2 * Math.PI);
    pts.push({ x, y });
  }
  return pts;
}

// ============================================================
// EKSPORT LINII ZWIERCIADŁA WODY MIĘDZY OTWORAMI
// (zw. swobodne + zw. napięte)
// ============================================================
function exportWaterLines(dxf, params) {
  if (!params.showWater) return;
  if (!params.showFreeWaterLine && !params.showConfinedWaterLine) return;
  const { boreholes } = params;
  if (!boreholes || boreholes.length < 2) return;

  const BLUE = 5;

  // Zw. swobodne: najpłytsze ustabilizowane (depthFrom), fallback → seepage
  if (params.showFreeWaterLine) {
    const pts = boreholes
      .map(bh => {
        const stab = (bh.waterLevels || []).filter(w => w.type === 'stabilized-drilled' && w.depthFrom != null);
        let depth = null;
        if (stab.length > 0) depth = Math.min(...stab.map(w => w.depthFrom));
        else {
          const seep = (bh.waterLevels || []).filter(w => w.type === 'seepage' && w.depth != null);
          if (seep.length > 0) depth = Math.min(...seep.map(w => w.depth));
        }
        if (depth === null) return null;
        return { x: bh.distance, y: bh.depth - depth };
      })
      .filter(p => p !== null);

    if (pts.length >= 2) {
      dxf.addLWPolyline(
        pts.map(p => ({ point: point2d(p.x, p.y) })),
        { layerName: 'WODA', colorNumber: BLUE }
      );
    }
  }

  // Zw. napięte: najpłytsze nawiercone (depthTo), fallback → seepage
  if (params.showConfinedWaterLine) {
    const pts = boreholes
      .map(bh => {
        const stab = (bh.waterLevels || []).filter(w => w.type === 'stabilized-drilled' && w.depthTo != null);
        let depth = null;
        if (stab.length > 0) depth = Math.min(...stab.map(w => w.depthTo));
        else {
          const seep = (bh.waterLevels || []).filter(w => w.type === 'seepage' && w.depth != null);
          if (seep.length > 0) depth = Math.min(...seep.map(w => w.depth));
        }
        if (depth === null) return null;
        return { x: bh.distance, y: bh.depth - depth };
      })
      .filter(p => p !== null);

    if (pts.length >= 2) {
      dxf.addLWPolyline(
        pts.map(p => ({ point: point2d(p.x, p.y) })),
        { layerName: 'WODA', colorNumber: BLUE }
      );
    }
  }
}

// ============================================================
// DOWNLOAD HELPER
// ============================================================
function downloadDxf(dxfString, filename) {
  const blob = new Blob([dxfString], { type: 'application/dxf' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

// ============================================================
// GŁÓWNA FUNKCJA EKSPORTU - V2
// Kolejność: warstwy gruntu NAJGŁĘBIEJ (pierwsze w DXF),
// reszta elementów rysowana nad nimi.
// ============================================================
export function exportToDxfV2({
  boreholes,
  scale,
  horizontalScale,
  connectLayers,
  findLayerConnections,
  interpolationPoints,
  connectionPointAdjustments,
  lenses,
  drawings,
  terrainProfile,
  manualConnections,
  resolvePointSvg,
  // Display toggles
  showGrid,
  showLenses,
  showBoreholeRectangles,
  showDepths,
  showSoilName,
  showSoilState,
  showGeotechnicalLayer,
  showThickness,
  showTerrain,
  showWater,
  showFreeWaterLine,
  showConfinedWaterLine,
  useHatchPatterns = true,
}) {
  if (!DXF_EXPORT_V2_ENABLED) {
    alert('Eksport DXF v2 jest wyłączony (DXF_EXPORT_V2_ENABLED = false)');
    return;
  }

  if (!boreholes || boreholes.length === 0) {
    alert('Brak otworów do eksportu');
    return;
  }

  console.log('[DXF v2] === START EKSPORTU ===');
  console.log('[DXF v2] boreholes:', boreholes.length, 'connectLayers:', connectLayers, 'manualConnections:', (manualConnections || []).length);

  const dxf = new DxfWriter();

  // Jednostki = metry
  dxf.setVariable('$INSUNITS', { 70: Units.Meters });

  const pixelsPerMeter = scale / 10;
  const maxDepth = getMaxDepthUtil(boreholes);

  // Tworzenie warstw DXF
  dxf.addLayer('OTWORY', Colors.White);
  dxf.addLayer('WARSTWY_WYPELNIENIE', Colors.Yellow);
  dxf.addLayer('WARSTWY_OPIS', Colors.White);
  dxf.addLayer('POLACZENIA', Colors.Green);
  dxf.addLayer('SOCZEWKI', Colors.Cyan);
  dxf.addLayer('TOPOGRAFIA', Colors.Red);
  dxf.addLayer('SIATKA', Colors.White);
  dxf.addLayer('RYSUNKI', Colors.Magenta);
  dxf.addLayer('GLEBOKOSCI', Colors.White);
  dxf.addLayer('FREEDRAW', Colors.Magenta);
  dxf.addLayer('SKALA', Colors.White);
  dxf.addLayer('WODA', Colors.Blue);

  const commonParams = {
    boreholes, maxDepth, pixelsPerMeter, horizontalScale,
    connectLayers, findLayerConnections,
    interpolationPoints, connectionPointAdjustments,
    lenses, drawings, terrainProfile,
    manualConnections, resolvePointSvg,
    showGrid, showLenses, showBoreholeRectangles,
    showDepths, showSoilName, showSoilState,
    showGeotechnicalLayer, showThickness, showTerrain,
    showWater, showFreeWaterLine, showConfinedWaterLine,
    useHatchPatterns,
  };

  // Eksport w kolejności z-order (od dna do wierzchu):
  // 1. Warstwy gruntu (interpolacje/połączenia) — NAJGŁĘBIEJ
  exportConnections(dxf, commonParams);
  // 2. Wypełnienia freedraw
  exportFreeDrawPolygons(dxf, commonParams);
  // 3. Soczewki geologiczne
  exportLenses(dxf, commonParams);
  // 4. Topografia (NMT)
  exportTerrain(dxf, commonParams);
  // 5. Siatka pomocnicza
  exportGrid(dxf, commonParams);
  // 6. Linie zwierciadła wody między otworami (pod otworami, nad siatką)
  exportWaterLines(dxf, commonParams);
  // 7. Otwory wiertnicze (pionowe linie i opisy warstw)
  exportBoreholes(dxf, commonParams);
  // 8. Symbole wody przy otworach (trójkąty, sączenia) — nad otworami
  exportWaterLevels(dxf, commonParams);
  // 9. Rysunki użytkownika (linie, strzałki, tekst) — NA WIERZCHU
  exportDrawings(dxf, commonParams);
  // 10. Skale i podziałki
  exportScales(dxf, commonParams);

  // Generuj i pobierz
  const dxfString = dxf.stringify();
  downloadDxf(dxfString, 'przekroj_geologiczny_v2.dxf');
}
